import type { Contractor } from "@/data/types";
import type { ComplianceDocument } from "./complianceDocuments";
import type { RequirementStatus } from "./complianceStatus";
import { getComplianceStatus } from "./complianceStatus";
import { hasRequiredAgreementSignature } from "@/data/contractor/onboarding/onboardingAgreements";
import type { OnboardingSession } from "@/data/contractor/onboarding/types";

export type ContractorComplianceListTone = "ok" | "review";

export interface ContractorComplianceListStatus {
  tone: ContractorComplianceListTone;
  label: "OK" | "Review";
  reason: "db_compliance" | "legacy_fields";
}

function latestSessionForContractor(
  sessions: OnboardingSession[],
  contractorId: string,
): OnboardingSession | undefined {
  return sessions
    .filter((session) => session.contractorId === contractorId)
    .sort((a, b) => Date.parse(b.lastTouchedAt) - Date.parse(a.lastTouchedAt))[0];
}

function rollUpRequirementStatuses(statuses: RequirementStatus[]): ContractorComplianceListTone {
  const required = statuses.filter((status) => status !== "not_required");
  if (!required.length) return "ok";
  return required.every((status) => status === "ok") ? "ok" : "review";
}

function hasDbBackedEvidence(
  contractorId: string,
  documents: ComplianceDocument[],
  session: OnboardingSession | undefined,
): boolean {
  return documents.some((doc) => doc.contractorId === contractorId) || Boolean(session);
}

function legacyStatus(contractor: Contractor): ContractorComplianceListStatus {
  const ok = contractor.insuranceOnFile && contractor.w9OnFile && contractor.agreementSigned;
  return {
    tone: ok ? "ok" : "review",
    label: ok ? "OK" : "Review",
    reason: "legacy_fields",
  };
}

export function getContractorComplianceListStatus(
  contractor: Contractor,
  documents: ComplianceDocument[],
  sessions: OnboardingSession[],
  parent?: Pick<Contractor, "id" | "entityType">,
): ContractorComplianceListStatus {
  const session = latestSessionForContractor(sessions, contractor.id);
  if (!hasDbBackedEvidence(contractor.id, documents, session)) {
    return legacyStatus(contractor);
  }

  const resolved = getComplianceStatus(contractor, documents, parent);
  const statuses = resolved.requirements
    // The /contractors compliance badge mirrors the compliance document page.
    // Background checks and payout verification are optional/onboarding review
    // signals, not document compliance blockers.
    .filter(
      (item) =>
        item.requirement !== "background_check" &&
        item.requirement !== "banking_verification",
    )
    .map((item) => {
      if (item.requirement === "agreement" && item.status !== "ok" && session && hasRequiredAgreementSignature(session)) {
        return "ok";
      }
      return item.status;
    });

  const tone = rollUpRequirementStatuses(statuses);
  return {
    tone,
    label: tone === "ok" ? "OK" : "Review",
    reason: "db_compliance",
  };
}
